<script setup lang="ts">
import { NuxtLink } from "#components";

defineProps<{
  items: any[];
  profileItems: any[];
}>();

const auth = useAuthStore();

const emit = defineEmits( ["close"] );

function generateId ( item, parentItem = null ) {
  let result = "main-navigation__";

  if ( parentItem && parentItem.label && typeof parentItem.label === "string" ) {
    const parentLabel = parentItem.label;
    result += parentLabel.indexOf( " " ) > -1
      ? `${parentLabel.replaceAll( " ", "-" )}-`
      : `${parentLabel}-`;
  }

  if ( item && item.label && typeof item.label === "string" ) {
    const itemLabel = item.label;
    result += itemLabel.indexOf( " " ) > -1
      ? itemLabel.replaceAll( " ", "-" )
      : itemLabel;
  }

  result += "--mobile";

  return result.toLowerCase();
}

const route = useRoute();

watch( () => route.fullPath, () => {
  emit( "close" );
} );
</script>

<template>
  <nav
    class="main-navigation"
    :class="auth.isLoggedIn ? '' : 'main-navigation--logged-out'">
    <ul class="main-navigation__list">
      <li
        v-for="(item, index) in items"
        :key="index"
        class="main-navigation__item"
        :class="{
          'main-navigation__item--separator': item.separator,
        }"
        @click="emit('close')">
        <NuxtLink
          v-if="item.url"
          :id="generateId(item)"
          class="main-navigation__link"
          :class="{
            'main-navigation__link--uppercase': item.uppercase,
          }"
          :to="item.url"
          :target="item.target || '_self'"
          :external="isMediaAssetUrl(item.url)">
          {{ item.label }}
        </NuxtLink>
        <span
          v-else
          :id="generateId(item)"
          class="main-navigation__link"
          :class="{
            'main-navigation__link--uppercase': item.uppercase,
          }">
          {{ item.label }}
        </span>

        <ul
          class="navigation-dropdown">
          <li
            v-for="(subItem, subIndex) in item.items"
            :key="subIndex"
            class="navigation-dropdown__item">
            <NuxtLink
              v-if="subItem.url"
              :id="generateId(subItem, item)"
              class="navigation-dropdown__link"
              :to="subItem.url"
              :target="subItem.target || '_self'"
              :external="isMediaAssetUrl(subItem.url)">
              {{ subItem.label }}
            </NuxtLink>
            <span
              v-else
              :id="generateId(subItem, item)"
              class="navigation-dropdown__link">
              {{ subItem.label }}
            </span>
          </li>
        </ul>
      </li>
      <li
        v-if="!auth.isLoggedIn"
        class="main-navigation__item">
        <a
          href="/login"
          class="
          router-link-active
          router-link-exact-active
          main-navigation__link
          main-navigation__link--uppercase">Log in</a>
      </li>
    </ul>

    <template v-if="auth.isLoggedIn">
      <div class="profile-wrapper">
        <ul
          class="navigation-dropdown">
          <li
            v-for="(item, index) in profileItems"
            :key="index"
            class="navigation-dropdown__item">
            <span class="navigation-dropdown__button-wrapper">
              <a-button
                :id="generateId(item)"
                size="md"
                pill
                :variant="item.variant || 'primary'"
                class="main-navigation__button"
                :to="item.url">
                {{ item.label }}
              </a-button>
            </span>
          </li>
        </ul>
      </div>
    </template>
  </nav>
</template>

<style lang="scss" scoped>
@import '~/assets/styles/_variables.scss';

.main-navigation {
  display: flex;
  align-items: stretch;
  justify-content: flex-start;
  position: relative;
  flex-direction: column;
  min-height: 100%;

  &__list {
    padding: 0;
    list-style: none;
    display: flex;
    margin: 0;
    flex-wrap: wrap;
    align-items: stretch;
    flex-direction: column;
    margin-top: 50px;
  }

  &__item {
    display: flex;
    position: relative;
    border-top: 4px solid transparent;
    border-bottom: 4px solid transparent;
    flex-direction: column;
    align-items: flex-start;

    &--separator {
      border-left: 1px solid #f2f2f2;
      border-left: 0;
      border-top: 1px solid #f2f2f2;
    }
  }

  &__link {
    display: inline-block;
    width: 100%;
    padding: 10px;
    white-space: nowrap;
    font-weight: 500;
    font-size: 1.2rem;
    text-decoration: none;
    cursor: pointer;

    &--uppercase {
      text-transform: uppercase;
    }
  }

  a.main-navigation__link:hover {
    background: color-mix( in srgb, var(--primary) 10%, transparent );
  }

  &__button {
    display: block;
    white-space: nowrap;
    margin-top: 15px;
    padding: 10px 20px;
    font-size: 1rem;
    align-self: stretch;
  }

  &--logged-out {
    flex-direction: column-reverse;
    justify-content: flex-end;
  }
}

.profile-wrapper {
  display: flex;
  align-items: center;
}

.navigation-dropdown {
  visibility: visible;
  display: flex;
  flex-direction: column;
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;

  &__link {
    padding: 7px 10px;
    display: block;
    border-bottom: 1px solid #eee;
    text-decoration: none;

    &:hover {
      background: color-mix( in srgb, var(--primary) 10%, transparent );
    }
  }

  &__button-wrapper {
    display: block;
    padding: 10px 15px;
  }

  &__item:last-child {
    .navigation-dropdown__button-wrapper {
      padding-bottom: 10px;
    }
  }

  .main-navigation__button {
    margin: 0;
  }

  &--open {
    visibility: visible;
  }
}

.dark-mode .main-navigation__item--separator {
  border-top: 0;
}

.dark-mode .navigation-dropdown__link {
  border-bottom-color: $dark-border-color !important;
}
</style>
